import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { getStartupValidationSnapshot, subscribeToStartupValidation } from '../util/startupValidation';
import { WidgetEnabled } from '../util/ToolbarUtils';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const indicatorBackground = (status) => {
  if (status === 'success') return '#2e9d50';
  if (status === 'error') return '#d64040';
  if (status === 'skipped') return '#78909c';
  return 'transparent';
};

const Overlay = styled.div`
  position: fixed;
  z-index: 20000;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 25vh 1rem 1rem;
  background: rgba(0, 0, 0, 0.25);
  opacity: ${({ $closing }) => ($closing ? 0 : 1)};
  pointer-events: ${({ $closing }) => ($closing ? 'none' : 'auto')};
  transition: opacity 350ms ease;
`;

const Panel = styled.div`
  width: min(100%, 420px);
  padding: 1.5rem;
  color: #f5f5f5;
  background: rgba(15, 24, 30, 0.75);
  border: 1px solid #f5f5f5;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.28);
  font-family: inherit;
  transform: translateY(-50%);
`;

const Title = styled.h2`
  margin: 0 0 1.1rem;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
`;

const Checks = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const CheckRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 24px;
  font-size: 0.95rem;
`;

const Indicator = styled.span`
  display: inline-grid;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  place-items: center;
  box-sizing: border-box;
  border: ${({ $status }) => ($status === 'pending' ? '2px solid rgba(245, 245, 245, 0.45)' : '0')};
  border-top-color: ${({ $status }) => ($status === 'pending' ? '#f5f5f5' : 'transparent')};
  border-radius: ${({ $status }) => ($status === 'warning' ? '0' : '50%')};
  color: ${({ $status }) => ($status === 'warning' ? '#263238' : '#fff')};
  background: ${({ $status }) => indicatorBackground($status)};
  animation: ${({ $status }) => ($status === 'pending' ? spin : 'none')} 800ms linear infinite;
`;

const IndicatorIcon = styled.svg`
  display: block;
  width: ${({ $status }) => ($status === 'warning' ? '18px' : '12px')};
  height: ${({ $status }) => ($status === 'warning' ? '18px' : '12px')};
  overflow: visible;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const StartupValidationModal = () => {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState(getStartupValidationSnapshot);

  useEffect(() => subscribeToStartupValidation(setSnapshot), []);

  if (!WidgetEnabled('STARTUP_CHECK')) return null;

  return (
    <Overlay $closing={snapshot.isClosing} aria-live="polite">
      <Panel role="dialog" aria-modal="true" aria-labelledby="startup-validation-title">
        <Title id="startup-validation-title">{t('startup.title')}</Title>
        <Checks>
          {snapshot.checks.map(({ key, label, status }) => {
            const visibleStatus = snapshot.statusFeedbackReady ? status : 'waiting';
            const labelKey = `startup.${key}`;

            return (
              <CheckRow key={key}>
                <span>{t(labelKey, { defaultValue: label })}</span>
                {snapshot.statusFeedbackReady && (
                  <Indicator $status={visibleStatus} aria-label={t(`startup.status.${visibleStatus}`)}>
                    {visibleStatus !== 'pending' && (
                      <IndicatorIcon $status={visibleStatus} viewBox="0 0 16 16" aria-hidden="true">
                        {visibleStatus === 'success' && <path d="M3.5 8.25 6.5 11.25 12.5 4.75" fill="none" />}
                        {visibleStatus === 'error' && <path d="m4.5 4.5 7 7m0-7-7 7" fill="none" />}
                        {visibleStatus === 'warning' && (
                          <>
                            <path d="M8 1.75 14.5 13H1.5L8 1.75Z" fill="#e6b800" />
                            <path d="M8 5.2v3.5" />
                            <circle cx="8" cy="11" r="0.6" fill="currentColor" stroke="none" />
                          </>
                        )}
                        {visibleStatus === 'skipped' && <path d="M5 8h6" fill="none" />}
                      </IndicatorIcon>
                    )}
                  </Indicator>
                )}
              </CheckRow>
            );
          })}
        </Checks>
      </Panel>
    </Overlay>
  );
};

export default StartupValidationModal;
